import ChartBarFloating16 from '@carbon/icons-react/es/chart--bar-floating/16';
import EventsAlt20 from '@carbon/icons-react/es/events--alt/20';
import Task16 from '@carbon/icons-react/es/task/16';
import UserMultiple16 from '@carbon/icons-react/es/user--multiple/16';
import { stringify } from 'query-string';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import {
  CompactMenu,
  CompactMenuTitle,
} from 'components/atoms/menu/compact-menu';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { ViewPresetId } from 'components/organisms/resource-management/scheduler-config/view-presets/constants';
import { ValidViewPresets } from 'components/organisms/resource-management/scheduler-config/view-presets/view-presets';
import {
  from_hash,
  viewPresetTransformer,
} from 'components/organisms/resource-management/transformers';
import { groupByTransformer } from 'components/organisms/resource-management/transformers/group-by-transfomer';
import { GroupBy } from 'components/organisms/resource-management/types';
import { Argon } from 'components/templates';
import { ArgonProps } from 'components/templates/argon';
import { RoutePaths } from 'constant';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import { useLocalStorage } from 'hooks/use-local-storage';

const DemandTabKey = 'demand';

interface Props extends Omit<ArgonProps, 'title' | 'tabs'> {
  currentGroupBy?: GroupBy;
  currentViewPreset?: ViewPresetId;
}

export const ResourceManagementPage: FC<Props> = ({
  content,
  actions,
  currentGroupBy,
  currentViewPreset,
}) => {
  const [zoomHash] = useLocalStorage('resource_management.zoom_level', '');
  const { push } = useHistory();
  const onTabSwitch = useCallback(
    ({ key }) => {
      if (key === DemandTabKey) {
        push(`${RoutePaths.resourceManagement}/demand`);
      } else {
        push({
          pathname: RoutePaths.resourceManagement,
          hash: `#${stringify({
            ...groupByTransformer.to(key as GroupBy),
            ...(currentViewPreset != null
              ? viewPresetTransformer(ValidViewPresets).to(currentViewPreset)
              : viewPresetTransformer(ValidViewPresets).to(
                  from_hash(ValidViewPresets)(zoomHash),
                )),
          })}`,
        });
      }
    },
    [currentViewPreset, push, zoomHash],
  );
  const isCollaborator = useIsCurrentUserCollaborator();

  const pageTitleId = isCollaborator
    ? 'manage_capacity.title'
    : 'resource_management.title';

  return (
    <Argon
      title={
        <HeaderWithIconTitle
          icon={<EventsAlt20 />}
          title={<FormattedMessage id={pageTitleId} />}
        />
      }
      tabs={
        <CompactMenu
          mode="horizontal"
          selectedKeys={[currentGroupBy ?? DemandTabKey]}
          onClick={onTabSwitch}
        >
          <CompactMenu.Item key={GroupBy.Users} data-cy="rm-tab-people">
            <CompactMenuTitle
              icon={<UserMultiple16 />}
              text={<FormattedMessage id="resource_management.tabs.users" />}
            />
          </CompactMenu.Item>
          <CompactMenu.Item key={GroupBy.Projects} data-cy="rm-tab-projects">
            <CompactMenuTitle
              icon={<Task16 />}
              text={<FormattedMessage id="resource_management.tabs.projects" />}
            />
          </CompactMenu.Item>
          {!isCollaborator && (
            <CompactMenu.Item key={DemandTabKey} data-cy="rm-tab-demand">
              <CompactMenuTitle
                icon={<ChartBarFloating16 />}
                text={<FormattedMessage id="resource_management.tabs.demand" />}
              />
            </CompactMenu.Item>
          )}
        </CompactMenu>
      }
      actions={actions}
      content={content}
    />
  );
};
